import React, { useContext, useMemo } from 'react';

import { BookingDetailsContext } from '@components/admin/booking/helpers';
import { Checkbox } from '@components/Table/Checkbox';
import { invoiceStrings } from '@data/invoice';
import { ledgerStrings } from '@data/ledger';
import { useConfirmDialog } from '@hooks/useActionableDialog';
import { countryStrings } from '@models/Country';
import type { InvoiceDetails } from '@models/next/Invoice';
import { InvoiceHelper } from '@models/next/InvoiceHelper';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { foldMessagesEither } from '@utils/errors';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import { formatPrice } from '@utils/types';
import clsx from 'clsx';
import Color from 'color';

const strings = createStrings({
  ja: {
    notAuthorized: 'あなたは当項目を編集する権限がありません。',
    locked: '当項目はロックされています。',
    payerIs: '請求先は',
    errorInvoiceDetailsUsed: '当項目は他の請求書に入れてあります。',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  invoiceDetailsTable: {
    ...common.middleText,
    display: 'grid',
    gridTemplateColumns: '40px 36px 1.5fr 80px 1fr 1fr 1fr 1fr',
    '& > div > div': {
      padding: '0.75rem',
    },
    '& > div:not(:last-child):not(:first-child) > div': {
      borderBottom: `1px solid ${theme.palette.grey[400]}`,
    },
    '&.panel': {
      gridTemplateColumns: '56px 1fr 1fr 2fr 100px 1fr 1fr 1fr 1fr 60px',
    },
  },
  invoiceDetailsHead: {
    '.grey &': {
      backgroundColor: theme.palette.base[200],
    },
  },
  invoiceDetailsCell: {
    ...common.textGray700,
    '$payerMarkJP &': {
      backgroundColor: Color(theme.palette.primary.main).alpha(0.1).toString(),
      '$selected&': {
        backgroundColor: Color(theme.palette.primary.main).alpha(0.2).toString(),
      },
    },
    '$payerMarkCN &': {
      backgroundColor: Color(theme.palette.accent.focus).alpha(0.08).toString(),
      '$selected&': {
        backgroundColor: Color(theme.palette.accent.focus).alpha(0.2).toString(),
      },
    },
    '$zero &': {
      opacity: 0.3,
    },
  },
  invoiceDetailsTableLegendsContainer: {
    ...common.flexInline,
    ...common.middleText,
    ...common.textBlack,
    justifyContent: 'flex-end',
    columnGap: '1rem',
    marginBottom: '1rem',
  },
  invoiceDetailsTableLegendIcon: {
    border: `1px solid ${theme.palette.grey[300]}`,
    height: 20,
    width: 20,
    marginRight: 8,
    display: 'inline-block',
    verticalAlign: 'bottom',
    '$payerMarkJP &': {
      backgroundColor: Color(theme.palette.primary.main).alpha(0.1).toString(),
    },
    '$payerMarkCN &': {
      backgroundColor: Color(theme.palette.accent.focus).alpha(0.08).toString(),
    },
  },
  checkboxNumIcon: {
    ...common.middleText,
    ...common.fontWeight700,
    ...common.flexCenter,
    color: theme.palette.primary.contrastText,
    height: '24px',
    width: '24px',
    padding: '3px',
    borderRadius: '5px',
    backgroundClip: 'content-box',
    '$payerMarkCN &': {
      backgroundColor: theme.palette.accent.focus,
    },
    '$payerMarkJP &': {
      backgroundColor: theme.palette.primary.main,
    },
  },
  checkbox: {
    position: 'relative',
    top: -1,
    margin: -9,
  },
  disabled: {},
  payerMarkJP: {},
  countryJP: {},
  payerMarkCN: {},
  countryCN: {},
  zero: {},
  selected: {},
  link: {
    ...common.link,
    '$countryCN &': {
      ...common.textAccentFocus,
    },
    '$countryJP &': {
      ...common.textPrimary,
    },
  },
}));

export interface CheckboxNumIconProps {
  number: number;
  className?: string;
}

export const CheckboxNumIcon: React.FC<CheckboxNumIconProps> = (props) => {
  const classes = useStyles();

  const { number } = props;
  return <div className={classes.checkboxNumIcon}>{number}</div>;
};

export interface InvoiceDetailsTables2Props {
  items: InvoiceDetails[];
  selectedIds?: number[];
  /**
   * Invoice details already in another invoice
   */
  usedIds?: number[];
  disabledIds?: number[];
  allSelectableIds?: number[];
  onChangeSelectedIds?: (ids: number[]) => void;
  type?: 'panel' | 'choices';
  color?: 'grey' | 'transparent';
  onClickEdit?: (v: InvoiceDetails) => void;
  ordered?: boolean;
}

export const InvoiceDetailsTable: React.FC<InvoiceDetailsTables2Props> = (props) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const {
    items,
    selectedIds = [],
    usedIds = [],
    disabledIds = [],
    allSelectableIds = [],
    type = 'choices',
    color = 'transparent',
    onChangeSelectedIds,
    onClickEdit,
    ordered = false,
  } = props;

  const { user, bookingInquiry, preAlert } = useContext(BookingDetailsContext);

  const allSelectable = allSelectableIds.length > 0;

  const allSelected = useMemo(
    () => allSelectable && allSelectableIds.every((s) => selectedIds.includes(s)),
    [allSelectable, selectedIds],
  );

  const confirmDialog = useConfirmDialog();

  const renderLegends = () => (
    <div className={classes.invoiceDetailsTableLegendsContainer}>
      <div className={classes.payerMarkJP}>
        <div className={classes.invoiceDetailsTableLegendIcon} />

        {strings.payerIs}
        {countryStrings.jp}
      </div>

      <div className={classes.payerMarkCN}>
        <div className={classes.invoiceDetailsTableLegendIcon} />

        {strings.payerIs}
        {countryStrings.cn}
      </div>
    </div>
  );

  return (
    <>
      {confirmDialog.renderDialog()}

      {renderLegends()}

      <div className={clsx(type, color, classes.invoiceDetailsTable)}>
        <div style={{ display: 'contents' }}>
          {type === 'choices' && (
            <div className={classes.invoiceDetailsHead}>
              <Checkbox
                disabled={!allSelectable}
                value={allSelected}
                onChange={(value) => onChangeSelectedIds?.(value ? allSelectableIds : [])}
              />
            </div>
          )}
          <div className={classes.invoiceDetailsHead}>{invoiceStrings.no}</div>
          <div className={classes.invoiceDetailsHead}>{invoiceStrings.invoice_name}</div>
          {type === 'panel' && (
            <div className={classes.invoiceDetailsHead}>{ledgerStrings.division}</div>
          )}
          {type === 'panel' && (
            <div className={classes.invoiceDetailsHead}>
              {invoiceStrings.invoice_name_preparation}
            </div>
          )}
          <div className={classes.invoiceDetailsHead}>{invoiceStrings.unit_price}</div>
          <div className={classes.invoiceDetailsHead}>{invoiceStrings.unit_num}</div>
          <div className={classes.invoiceDetailsHead}>{invoiceStrings.unit_num_type}</div>
          <div className={classes.invoiceDetailsHead}>{invoiceStrings.excise_tax}</div>
          <div className={classes.invoiceDetailsHead}>{invoiceStrings.tax_excluded_price}</div>
          {type === 'panel' && <div className={classes.invoiceDetailsHead} />}
        </div>

        {items.map((item, i) => {
          const { unitPrice, currency, exciseTax, taxExcludedPrice } =
            InvoiceHelper.getInvoiceDetailsComputed(item);
          const selected = selectedIds.includes(item.id);

          const canEdit = InvoiceHelper.canEditInvoiceDetail(bookingInquiry, preAlert, user, item);
          const itemEditable = canEdit._tag === 'Right';
          const disabled = disabledIds.includes(item.id);
          const used = usedIds.includes(item.id);

          return (
            <div
              key={item.id}
              style={{ display: 'contents' }}
              className={clsx(
                item.payer_mark === 'cn' && classes.payerMarkCN,
                item.payer_mark === 'jp' && classes.payerMarkJP,
                item.country === 'cn' && classes.countryCN,
                item.country === 'jp' && classes.countryJP,
                item.unit_num === 0 && classes.zero,
                item.payer_mark === 'jp' && !(disabled || used) && classes.contentsHoverTextPrimary,
                item.payer_mark === 'cn' &&
                  !(disabled || used) &&
                  classes.contentsHoverTextAccentFocus,
                selected && classes.selected,
              )}
              data-testid="invoiceDetailEach"
            >
              {type === 'choices' && (
                <div
                  className={clsx(
                    classes.invoiceDetailsCell,
                    item.payer_mark === 'cn' && classes.textAccentFocus,
                  )}
                  onClick={() =>
                    used && confirmDialog.confirm({ message: strings.errorInvoiceDetailsUsed })
                  }
                >
                  {used ? (
                    <Checkbox disabled value={true} />
                  ) : (
                    <Checkbox
                      value={selected}
                      checkedIcon={
                        ordered ? (
                          <CheckboxNumIcon number={selectedIds.indexOf(item.id) + 1} />
                        ) : undefined
                      }
                      disabled={disabled}
                      className={classes.checkbox}
                      onClick={() => {
                        if (selected) {
                          onChangeSelectedIds?.(selectedIds.filter((id) => id !== item.id));
                        } else {
                          onChangeSelectedIds?.([...selectedIds, item.id]);
                        }
                      }}
                    />
                  )}
                </div>
              )}

              <div className={classes.invoiceDetailsCell} data-testid="invoiceDetailNo">
                {i + 1}
              </div>
              <div className={classes.invoiceDetailsCell} data-testid="invoiceDetailName">
                {item.invoice_name}
              </div>
              {type === 'panel' && (
                <div className={classes.invoiceDetailsCell} data-testid="invoiceDetailDebit">
                  {item.debit}
                </div>
              )}
              {type === 'panel' && (
                <div
                  className={classes.invoiceDetailsCell}
                  data-testid="invoiceDetailNamePreparation"
                >
                  {item.invoice_name_preparation || '-'}
                </div>
              )}
              <div className={classes.invoiceDetailsCell} data-testid="invoiceDetailUnitPrice">
                {unitPrice != null ? formatPrice(unitPrice, currency) : '-'}
              </div>
              <div className={classes.invoiceDetailsCell} data-testid="invoiceDetailUnitNum">
                {item.unit_num ?? '-'}
              </div>
              <div className={classes.invoiceDetailsCell} data-testid="invoiceDetailUnitNumType">
                {item.unit_num_type}
              </div>
              <div className={classes.invoiceDetailsCell} data-testid="invoiceDetailExciseTax">
                {exciseTax != null ? formatPrice(exciseTax, currency) : '-'}
              </div>
              <div
                className={classes.invoiceDetailsCell}
                data-testid="invoiceDetailTaxExcludedPrice"
              >
                {taxExcludedPrice != null ? formatPrice(taxExcludedPrice, currency) : '-'}
              </div>

              {type === 'panel' && (
                <div className={classes.invoiceDetailsCell} data-testid="invoiceDetailEdit">
                  <a
                    className={itemEditable ? clsx(classes.link) : classes.linkDisabled}
                    onClick={() => {
                      foldMessagesEither(
                        canEdit,
                        ({ messages }) => {
                          confirmDialog.confirm({
                            message: messages,
                          });
                        },
                        () => {
                          onClickEdit?.(item);
                        },
                      );
                    }}
                  >
                    {formStrings.edit}
                  </a>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
};
